import { useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Head from "next/head";

import { ChannelQuery, ChannelQueryVariables } from "types/api";
import { trackLandingPageView } from "utils/analytics";
import { AdTemplatesEnum, registerAdsPageView } from "utils/ads";
import { withData, useQuery, gql, UrqlPageType } from "utils/urql";
import usePagination from "utils/usePagination";
import PageLayout from "components/PageLayout";
import ChannelPinnedArticle from "components/landing/ChannelPinnedArticle";
import ChannelEditorialProject from "components/landing/ChannelEditorialProject";
import LandingLayout from "components/landing/LandingLayout";
import LandingTitle from "components/landing/LandingTitle";
import LandingRiver from "components/landing/LandingRiver";
import LandingRailAd from "components/ads/LandingRail";
import NonMeteredNudge from "components/NonMeteredNudge";
import LeaderboardAd from "components/ads/Leaderboard";
import { throwCorrectStatus } from "utils/error";

const CHANNEL_QUERY = gql`
  ${LandingRiver.fragment}
  ${ChannelPinnedArticle.fragment}
  ${ChannelEditorialProject.fragment}

  query ChannelQuery(
    $slug: String!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    channel(slug: $slug) {
      id
      slug
      displayName
      grapeshot {
        segments
      }
      pinnedArticle {
        ...channelPinnedArticle
      }
      editorialProject {
        ...channelEditorialProject
      }
      river(first: $first, last: $last, after: $after, before: $before) {
        ...landingRiver
      }
    }
  }
`;

const useTrackPageView = ({
  slug,
  displayName,
  river: { edges },
  grapeshot,
}: ChannelQuery["channel"]) => {
  // Convert the edges to a string so we can compare it more reliably in the
  // useEffect dependencies.
  const memoizedEdges = useMemo(
    () => edges.map((edge) => edge?.node.id).join("-"),
    [edges]
  );

  // Rerun the effect if edges, displayName, or slug changes.
  useEffect(() => {
    // Track the pageview in GTM
    trackLandingPageView({ displayName });

    // Register the pageview in ads.js
    registerAdsPageView({
      channel: slug,
      template: AdTemplatesEnum.landing_channel,
      globalTargeting: {
        title: slug,
        grapeshot_segments: grapeshot?.segments,
      },
    });
  }, [displayName, memoizedEdges, slug]); //eslint-disable-line react-hooks/exhaustive-deps
};

const ChannelDisplay = ({
  data,
  fetching,
}: {
  data: ChannelQuery;
  fetching: boolean;
}) => {
  useTrackPageView(data.channel);

  const isFirstPage = !data.channel.river.pageInfo.hasPreviousPage;

  return (
    <>
      <Head>
        <title>{data.channel.displayName} - The Atlantic</title>
        <meta
          property="og:title"
          content={data.channel.displayName}
          key="og:title"
        />
        <meta
          property="og:url"
          content={`/${data.channel.slug}/`}
          key="og:url"
        />
        <meta
          property="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        <meta
          property="og:image"
          key="og:image"
          content="https://cdn.theatlantic.com/assets/static/a/theatlantic/img/lacroix-default-thumbnail.png"
        />
      </Head>
      <LeaderboardAd enableMobile={true} />

      <LandingLayout
        fetching={fetching}
        header={<LandingTitle>{data.channel.displayName}</LandingTitle>}
        pinnedArticle={
          isFirstPage &&
          data.channel.pinnedArticle && (
            <ChannelPinnedArticle {...data.channel.pinnedArticle} />
          )
        }
        editorialProject={
          isFirstPage &&
          data.channel.editorialProject?.river?.edges?.[0]?.node && (
            <ChannelEditorialProject {...data.channel.editorialProject} />
          )
        }
        river={<LandingRiver {...data.channel.river} />}
        sidebar={<LandingRailAd targetingPos="boxsticky" />}
      />
    </>
  );
};

const Channel: UrqlPageType = () => {
  const { query } = useRouter();
  const slug = query.channel as string;
  const { after, before, first, last } = usePagination();

  const [{ error, data, fetching }] = useQuery<
    ChannelQuery,
    ChannelQueryVariables
  >({
    query: CHANNEL_QUERY,
    variables: {
      slug,
      after,
      before,
      first,
      last,
    },
  });

  // If we're done fetching and there is still no data or no river, it's a 404.
  if (!fetching && (!data || !data.channel?.river.edges.length)) {
    throwCorrectStatus({ error });
  }

  return (
    <PageLayout analyticsCategory="landing page">
      {data && <ChannelDisplay data={data} fetching={fetching} />}
      <NonMeteredNudge />
    </PageLayout>
  );
};
export default withData(Channel);
